// src/pages/Login.jsx
import { use, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { useEffect } from "react";

export default function Login() {
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  const { user } = useAuth();
  
  useEffect(() => {
    if (user) navigate(from, { replace: true });
  }, [user, from, navigate]);

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    const cleanEmail = email.trim();

    try {
      if (!cleanEmail || !password) {
        setStatus("Email and password are required.");
        return;
      }

      const res =
        mode === "signup"
          ? await supabase.auth.signUp({ email: cleanEmail, password })
          : await supabase.auth.signInWithPassword({ email: cleanEmail, password });

      if (res.error) {
        setStatus(res.error.message);
        return;
      }

      // If confirmations are ON, user might not be logged in yet
      const hasSession = !!(await supabase.auth.getSession()).data.session;
      if (hasSession) navigate(from, { replace: true });
      else setStatus("Account created. Check email to confirm if required.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-md mx-auto bg-white p-6 rounded-lg shadow">
      <h1 className="text-xl font-bold mb-2">
        {mode === "signin" ? "Sign in" : "Create account"}
      </h1>

      <div className="flex gap-2 mb-4 text-sm">
        <button
          className={`px-3 py-1 rounded ${mode === "signin" ? "bg-gray-200" : "bg-transparent"}`}
          onClick={() => setMode("signin")}
          type="button"
        >
          Sign in
        </button>
        <button
          className={`px-3 py-1 rounded ${mode === "signup" ? "bg-gray-200" : "bg-transparent"}`}
          onClick={() => setMode("signup")}
          type="button"
        >
          Sign up
        </button>
      </div>

      {/* Free Features Messaging */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-lg font-semibold text-gray-900 mb-3">
          Free to start. No credit card. No commitment.
        </p>
        
        <div className="space-y-2 mb-3">
          <p className="text-sm text-gray-700">Get instant access to:</p>
          <ul className="text-sm text-gray-700 space-y-1">
            <li className="flex items-start">
              <span className="text-green-600 mr-2">✓</span>
              Social captions
            </li>
            <li className="flex items-start">
              <span className="text-green-600 mr-2">✓</span>
              Blog writing
            </li>
            <li className="flex items-start">
              <span className="text-green-600 mr-2">✓</span>
              Prewriting and idea help
            </li>
            <li className="flex items-start">
              <span className="text-green-600 mr-2">✓</span>
              Image prompts and ideas
            </li>
            <li className="flex items-start">
              <span className="text-green-600 mr-2">✓</span>
              Brand voice support
            </li>
          </ul>
        </div>
        
        <p className="text-xs text-gray-500">
          Pro features and image packs available after signup.
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-3">
        <input
          className="w-full border rounded px-3 py-2"
          placeholder="you@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
        />
        <input
          className="w-full border rounded px-3 py-2"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          type="password"
          autoComplete={mode === "signup" ? "new-password" : "current-password"}
        />

        <button
          className="w-full bg-sabre-blue text-white rounded py-2 disabled:opacity-50"
          disabled={loading}
          type="submit"
        >
          {loading ? "Working..." : mode === "signin" ? "Sign in" : "Create account"}
        </button>

        {status && <div className="text-sm text-gray-700">{status}</div>}
      </form>

      <button
        type="button"
        className="text-sm underline text-gray-600 mt-3"
        onClick={async () => {
          if (!email) {
            setStatus("Enter your email first.");
            return;
          }

          const { error } = await supabase.auth.resetPasswordForEmail(
            email.trim(),
            {
              redirectTo: "https://dobiecore-app.vercel.app/reset-password",
            }
          );

          setStatus(
            error
              ? error.message
              : "Check your email to set or reset your password."
          );
        }}
      >
        Forgot your password?
      </button>

      <p className="text-sm text-gray-600 mt-4">
        <Link to="/privacy" className="underline hover:text-gray-800">
          Privacy Notice
        </Link>
      </p>
    </div>
  );
}
